<template>
  <div>
    <!-- List Filters -->
    <k-layout gap="10px"> 
      <k-flex c6>
        <billing-customer-select 
          v-model="filters" 
          customer_name="customer" 
          customer_label="Billing customer" 
          contract_name="contract" 
          contract_label="Billing contract" 
          show_all_contracts="true" 
          show_last_billed="true"
        ></billing-customer-select>
        <div>
          <date-picker 
            :disabledAfter="filters.daterange.end" 
            first-day-of-week="1" 
            v-model="filters.daterange.start" 
            label="From"
          ></date-picker>
          <date-picker 
            :disabledBefore="filters.daterange.start" 
            first-day-of-week="1" 
            v-model="filters.daterange.end" 
            label="To"
          ></date-picker>
          <month-picker 
            @change="handleNewMonth($event)" 
            label="Month"
          ></month-picker>
        </div>
      </k-flex>
      <k-flex c4></k-flex>

      <k-flex c2>
          <div>Events: {{event_count}}</div>
          <div>Total sum: {{total_sum.toFixed(2)}}</div>
          <div>Billed: {{billed_sum.toFixed(2)}}</div>
          <div>Not billed: {{unbilled_sum.toFixed(2)}}</div>
          <div>Total expenses: {{expenses_sum.toFixed(2)}}</div>
      </k-flex>
    </k-layout>

    <!-- List Buttons -->
    <k-layout gap="10px">
      <k-flex c6 class="ks-billing-list-buttons">
        <!-- Filter menu -->
        <k-menu
          offset-y
          :close-on-content-click="false">
          <template slot="activator" slot-scope="{ on }" 
            style="padding: 20px;"
          >
            <k-btn v-on="on">
              <span style="display: flex; align-items: left;">
                <div>Filter</div>
                <k-icon small>filter_list</k-icon>
              </span>
            </k-btn>
          </template>
          <k-list :items="menu_items">
              <template #default="{ item }">
                <span>
                  <k-checkbox
                    v-model="menu_filters"
                    :label="item.label"
                    :input-value="item.value"
                  ></k-checkbox>
                </span>
              </template>
          </k-list>
        </k-menu>

        <!-- Actions menu -->
        <k-menu offset-y :close-on-content-click="false">
          <template v-slot:activator="{ on }">
            <k-btn v-on="on">Actions</k-btn>
          </template>
          <template #default>
            <div>Mark {{selected_events_size}} events</div>
            <k-divider/>
            <k-list 
              :items="actions_items" 
              @click="(item) => {mark_events_as_billed(item.value)}"
            >
              <template #left="{ item }">
                <k-icon>{{item.icon}}</k-icon>
              </template>
              <template #default="{ item }">
                  {{ item.text }}
              </template>
            </k-list>
          </template>
        </k-menu>

        <!-- Export CSV -->
        <k-btn 
          @click.stop="export_csv()" 
          :disabled="event_count == 0"
        >Export CSV</k-btn>
        
        <!-- Chart Button -->
        <k-btn
          @click.stop="toggle_chart()"
          value="show_dougnut_chart"
          :disabled="(expenses_sum == 0 && total_sum == 0)"
        >Chart</k-btn>
      </k-flex>
    </k-layout>

    <!-- Charts -->
    <chart
      :show_doughnut_chart="show_doughnut_chart"
      :total_sum="total_sum"
      :billed_sum="billed_sum"
      :unbilled_sum="unbilled_sum"
      :expenses_sum="expenses_sum"
      :billables_info="billables_info"
      :expenses_info="expenses_info"
      :sorted_billables_info="sorted_billables_info"
      :sorted_expenses_info="sorted_expenses_info"
    ></chart>

    <!-- Table -->
    <k-data-table 
      ref="events" 
      v-bind:pagination.sync="pagination"
      :loading="table_loading" 
      :search="search"
      :headers="headers"
      :items="event_list" 
      :arows-per-page-items='[50, 150, 250]'
      v-if="is_settings"
    >
      <!-- Table Headers -->
      <template #headers="props">
        <tr>
          <th class="ks-billing-table-headers-checkbox">
            <k-checkbox
              hide-details
              v-model="all_events_checked"
            ></k-checkbox>
          </th>
          <th
            :value="props.item"
            width="19%"
            style="padding: 0px 10px 0px 5px !important;"
          >Title</th>

          <th
            v-for="header in props.headers"
            :width="header.width"
            :key="header.text"
          >{{ header.text }}</th>
        </tr>
      </template>

      <!-- Table Items -->
      <template #items="props">
        <tr 
          :active="$route.params.id == props.item.uuid" 
          :class="if_archived_color(props.item.state)" 
          :value="props.item"
        >
          <!-- Item Checkbox -->
          <td style="padding: 0px 5px 0px 10px;">
            <k-checkbox  
              v-model="selected_events[props.item.uuid]" 
              hide-details
            ></k-checkbox>
          </td>

          <!-- Item Title -->
          <td 
            @click.stop="open_edit_event(props.item.uuid)"
            class="ks-billing-table-item-title"
            :key="props.item.title"
          >
            <div>{{props.item.title}}</div>
            <div class="ks-billing-table-item-subtitle"
            >{{props.item.subtitle}}&nbsp;</div>
          </td>

          <!-- Item Source -->
          <td @click.stop="open_edit_event(props.item.uuid)">
            {{props.item.input.title}}
          </td>

          <!-- Item Destination -->
          <td @click.stop="open_edit_event(props.item.uuid)">
            {{ props.item.output.title }}
          </td>

          <!-- Item Start time -->
          <td class="text-no-wrap"
            @click.stop="open_edit_event(props.item.uuid)"
          >
            <div>{{format_date(props.item.start_time) }}</div>
            <div>{{format_time(props.item.start_time)}}&nbsp;</div>
          </td>

          <!-- Item End time -->
          <td class="text-no-wrap"
            @click.stop="open_edit_event(props.item.uuid)"
          >
            <div>{{format_date(props.item.end_time) }}</div>
            <div>{{format_time(props.item.end_time)}}&nbsp;</div>
          </td>

          <!-- Item Duration -->
          <td class="text-no-wrap"
            @click.stop="open_edit_event(props.item.uuid)"
          >
            {{event_duration(
                props.item.start_time, 
                props.item.end_time, 
                props.item.kickoff_time, 
                props.item.include_open_before
              )}}
          </td>

          <!-- Item Contract -->
          <td class="text-no-wrap">
              <k-chip size="xs" 
                :class="contract_color(props.item.contract_type)" 
                @click.stop="open_contract_edit(props.item.contract_id)"
                v-if="props.item.contract_id"
              >{{ props.item.contract_title}}</k-chip>
          </td>

          <!-- Item Last billed -->
          <td @click.stop="open_edit_event(props.item.uuid)">
            {{ format_date(props.item.contract_last_billed)}}
          </td>

          <!-- Item Sum -->
          <td @click.stop="open_edit_event(props.item.uuid)">
            {{ props.item.sum}}
          </td>
          
          <!-- Item Icons: Custom sums & Extra info -->
          <td @click.stop="open_edit_event(props.item.uuid)">
            <k-icon small color="success"
              v-if="props.item.sum_from_event"
              v-tooltip.bottom="'Sum is defined in the event'"
            >euro_symbol</k-icon>
            <k-icon small color="danger"
              v-if="props.item.expenses_sum_from_event"
              v-tooltip.bottom="'Expenses sum is defined in the event'"
            >euro_symbol</k-icon>
            <k-icon small color="warning"
              v-if="props.item.is_additional_information"
              v-tooltip.bottom="'Check the event for extra information'"
            >warning</k-icon>
          </td>

          <!-- Item Billing Status -->
          <td class="text-no-wrap text-right"
            style="width: 50px;"
            @click.stop="open_edit_event(props.item.uuid)" 
          >
            <k-icon small color="success"
              v-if="props.item.billing_status == 'billed'"
              v-tooltip.bottom="'Billed'"
            >done</k-icon>
            <k-icon small 
              v-else 
              v-tooltip.bottom="'Not billed'"
            >remove_circle_outline</k-icon>
          </td>
        </tr>
      </template>
    </k-data-table>
  </div>
</template>

<script>

import * as FormItems from '@/components/form_items'

import datelib from '@/lib/date.js';
import moment from 'moment';
import DatePicker from '../DatePicker'
import MonthPicker from './components/MonthPicker.vue'
import EventModel from '@/models/event.js'
import ChannelModel from '@/models/channel.js'
import SettingsModel from '@/models/settings.js';
import Vue from 'vue';
import BillingCustomerSelect from '@/components/form_items/BillingCustomerSelect.vue';
import Chart from './components/Chart.vue';

export default {
  components: {
    DatePicker,
    MonthPicker,
    ...FormItems,
    BillingCustomerSelect,
    Chart
  },

  data () {
    var start_date = moment().startOf('month').startOf('day').toDate();
    var end_date = moment().endOf('month').endOf('day').toDate();
    return {
      ws_channel: undefined,
      items: [],
      contracts: {},
      contracts_by_customer: {},
      table_loading: false,
      events: [],
      event_list_cached: [],
      selected_events: {},
      selected: [],
      all_selected: false,
      pagination: {'sortBy': 'start_time', 'descending': false},
      total_sum: 0,
      unbilled_sum: 0,
      billed_sum: 0,
      billables_info: {},
      expenses_sum: 0,
      expenses_info: {},
      event_count: 0,
      filters: {
        customer: undefined,
        contract: undefined,
        states: ["active"],      
        daterange: {
          start: start_date,
          end: end_date
        }
      },
      headers: [
        /* Title header in template. width: 19%*/
        { text: 'Source', value: 'input.title', width: '13%'},
        { text: 'Destination', value: 'output.title', width: '13%' },
        { text: 'Start time', value: 'start_time', width: '6%' },
        { text: 'End time', value: 'end_time', width: '6%' },
        { text: 'Duration', value: 'duration', width: '6%' },
        { text: 'Contract', value: 'contract_title', width: '13%' },
        { text: 'Last billed', value: "contract_last_billed", width: '6%' },
        { text: 'Sum', value: 'sum', width: '6%' },
        { text: '', value: '', width: '6%' },
        { text: 'Billing status', value: 'billing_status' }
      ],
      actions_items: [
        {text: 'Billed', value: 'billed', icon: 'done'},
        {text: 'Not billed', value: 'not_billed', icon: 'remove_circle_outline'}
        //{text: 'Problem', value: 'problem', icon: 'warning'} Example of extra actions
      ],
      menu_items: [
        {label: 'Events without contract', value: 'events_without_contract'},
        {label: 'Billed events', value: 'billed'},
        {label: 'Unbilled events', value: 'not_billed'},
        {label: 'Extra information', value: 'extra_information'},
        {label: 'Custom sums', value: 'custom_sum'},
        {label: 'Cloud Productions', value: 'cloud_productions'}
      ],
      menu_filters: [],
      show_doughnut_chart: false
    }
  },
  watch: {
    "filters.daterange": {
      handler () {
        this.filter_events();
      },
      deep: true
    },
    '$route' (to,from) {
      if(to.path == "/billing/billing/list" && (from.path.indexOf("/billing/billing/content_edit/") != -1)) {
        this.update_contract_data(this.filter_events);
      }
    }
  },
  mounted () {
    this.connect_ws();
  },
  computed: {
    search () {
      return this.$store.state.search_text;
    },
    selected_events_size () {
      return _.size(_.filter(this.selected_events, v => v));
    },
    all_events_checked: {
      set: function(value) {
        this.check_all_events();
      },
      get: function() {
        let selected = _.filter(this.selected_events, v => v);
        if (typeof(this.$refs.events) == "undefined") {return;};
        if (_.size(selected) == _.size(this.$refs.events.filteredItems) && _.size(selected) != 0) { return true; } else { return false; }
      }
    },
    event_list () {
      var self = this;
      var events = this.events;
      var billable_info = {};
      var expense_info = {};

      this.billables_info = {};
      this.unbilled_sum = 0;
      this.billed_sum = 0;
      this.total_sum = 0;
      this.expenses_info = {};
      this.expenses_sum = 0;
      this.event_count = 0;
      this.selected_events = {};
      var ret = [];

      _.each(events, (event) => {
        // If there's no system_meta, there's no billing information either.
        if (_.isEmpty(event.system_meta) || _.isEmpty(event.meta)) {return;}
        // Only show billed events if so desired
        if (self.show_only_billed_events && event.system_meta.billing.status != "billed") {return;}
        // If status isn't yet defined, define as not_billed
        if (typeof(event.system_meta.billing.status) == "undefined") {
          event.system_meta.billing.status = "not_billed";
        }
        
        var kickoff_time = (event.event_group||{}).start_time;
        var start_time = event.cached_start_time;
        var end_time = event.cached_end_time;

        var contract = this.contracts[event.system_meta.billing.contract];
        var contract_type = "event";
        var menu_filters = this.menu_filters;
        
        var sum_from_event = false;
        var expenses_sum_from_event = false;
        var sum = 0.0;
        var expense_sum = 0.0;

        // Filtering for billing status
        if (menu_filters.includes("billed") || menu_filters.includes("not_billed")) {
          if (!menu_filters.includes(event.system_meta.billing.status)) {return;}
        }
        // If no contract, add channel default if available
        if (typeof(contract) == "undefined") {
          var channel = EventModel.output_channel(event);
          if (typeof(channel) == "undefined") {return;}
          if (((typeof(channel.meta.billing_contract) == "undefined") && typeof(channel.meta.billing_customer) != "undefined")) {
            var contracts = self.contracts_by_customer[channel.meta.billing_customer];
            if (typeof(contracts) != "undefined") {
              var contracts_list = _.filter(contracts, (c) => {
              if (start_time > c.start_date && start_time < c.end_date) {
                return true;
              }
              return false;
              });
              if (contracts_list.length == 1) {
                contract_type = "channel";
                contract = contracts_list[0];
              }
            }
          } else {
            contract_type = "channel";
            contract = this.get_contract_by_id(channel.meta.billing_contract);
          }
        }

        // Filtering for events without contract
        if (menu_filters.includes("events_without_contract")) {
          if (typeof(contract) != "undefined") {return;}
        }
        
        // Custom billing sum check
        if (typeof(event.system_meta.billing.sum) != "undefined" && event.system_meta.billing.sum != "") {
          sum = parseFloat(event.system_meta.billing.sum);
          sum_from_event = true;
        }

        // Custom expenses sum check
        if (typeof(event.system_meta.billing.expenses_sum) != "undefined" && event.system_meta.billing.expenses_sum != "") {
          expense_sum = parseFloat(event.system_meta.billing.expenses_sum);
          expenses_sum_from_event = true;
        }
        
        // Additional information check
        var is_additional_information = false;
        if (typeof(event.system_meta.billing.info) != "undefined" && event.system_meta.billing.info != "") {
          is_additional_information = true;
        }

        // Filtering for extra information and custom sums from events
        if ((menu_filters.includes("extra_information") && !is_additional_information)) {
          return;
        }
        if (menu_filters.includes("custom_sum") && !sum_from_event) {
          return;
        }
        if (menu_filters.includes("cloud_productions")) {
          var input_ch = EventModel.input_channel(event);
          if (ChannelModel.is_cloudstreamer(input_ch) !== true) {
            return;
          }
        }

        if (typeof(contract) == "undefined") {contract = {};}

        // Filtering for specific billing contract
        if (typeof(self.filters.contract) != "undefined") {
          if (contract.id != self.filters.contract) {return;}
        }
        
        // Filtering for specific billing customers contracts
        if (typeof(self.filters.customer) != "undefined") {
          if (_.has(contract, "customer")) {
            if (contract.customer.id == self.filters.customer) {} else {return;};
          } else {
            return;
          }
        }
        
        // Add information to billables info if no custom sum
        if (!sum_from_event) {
          sum = 0.0;
          if (typeof(contract.id) != "undefined") {
            var billables = contract.billables;
            if (typeof(billables) != "undefined") {
              _.each(billables, function(billable) {
                if (billable.data.type == "static") {
                  sum = sum + parseFloat(billable.data.price);
                  // Add information to the billables info if no sum defined in event
                  if (billable.data.name in (billable_info)) {
                    billable_info[billable.data.name].sum += parseFloat(billable.data.price);
                    billable_info[billable.data.name].number += 1;
                  } else {
                    billable_info[billable.data.name] = {"name": billable.data.name, "type": billable.data.type, "price": parseFloat(billable.data.price), "number": 1, "sum": parseFloat(billable.data.price)};
                  }
                }
                if (billable.data.type == "hour") {
                  var price = parseFloat(billable.data.price) / 3600.0;
                  var billable_price = 0;
                  var hours = 0.0;
                  // If inclusion of open before time is defined to be true
                  if (contract.include_open_before) {
                    billable_price = (end_time - start_time) * price
                    hours += (end_time - start_time) / 3600.0;
                  } else { // Else count duration from kickoff 
                    billable_price = (end_time - kickoff_time) * price
                    hours += (end_time - kickoff_time) / 3600.0;
                  }
                  sum += billable_price;
                  // Add information to the billables info if no sum defined in event
                  if (typeof(event.system_meta.billing.sum) != "undefined" && event.system_meta.billing.sum != "") {return;}
                  if (billable.data.name in (billable_info)) {
                    billable_info[billable.data.name].sum += billable_price;
                    billable_info[billable.data.name].number += hours;
                  } else {
                    billable_info[billable.data.name] = {"name": billable.data.name, "type": billable.data.type, "price": billable.data.price, "sum": billable_price, "number": hours};
                  }
                }
              });
            }
          }
        }

        // Add information to expenses info if no custom sum
        if (!expenses_sum_from_event) {
          expense_sum = 0.0
          if (typeof(contract.id) != "undefined") {
            var expenses = contract.expenses;
            if (typeof(expenses) != "undefined") {
              _.each(expenses, function(expense) {
                if (expense.data.type == "static") {
                  expense_sum = expense_sum + parseFloat(expense.data.price);
                  // Add information to the expenses info
                  if (expense.data.name in (expense_info)) {
                    expense_info[expense.data.name].sum += parseFloat(expense.data.price);
                    expense_info[expense.data.name].number += 1;
                  } else {
                    expense_info[expense.data.name] = {
                      "name": expense.data.name, 
                      "type": expense.data.type,  
                      "price": parseFloat(expense.data.price), 
                      "sum": parseFloat(expense.data.price), 
                      "number": 1};
                  }
                }
                if (expense.data.type == "hour") {
                  var price = parseFloat(expense.data.price) / 60.0 / 60.0;
                  var s = (end_time - start_time) * price
                  expense_sum = expense_sum + s;
                  // Add information to the expenses info
                  if (expense.data.name in (expense_info)) {
                    expense_info[expense.data.name].sum += s;
                    expense_info[expense.data.name].number += 1;
                  } else {
                    expense_info[expense.data.name] = {"name": expense.data.name, "type": expense.data.type, "sum": expense_sum, "price": parseFloat(expense.data.price), "number": 1};
                  }
                }
              });
            }
          }
        }
        
        // If custom billing sum, add to billable info
        if (typeof(event.system_meta.billing.sum) != "undefined" && event.system_meta.billing.sum != "") {
          sum = parseFloat(event.system_meta.billing.sum);
          // Add sum to billables info
          if ("custom_sums" in (billable_info)) {
            billable_info["custom_sums"].sum += parseFloat(event.system_meta.billing.sum);
          } else {
            billable_info["custom_sums"] = {"name": "Custom sums", "type": "custom", "sum": parseFloat(event.system_meta.billing.sum)};
          }                    
          sum_from_event = true;
        }

        // If custom expense sum, add to expense info
        if (typeof(event.system_meta.billing.expenses_sum) != "undefined" && event.system_meta.billing.expenses_sum != "") {
          expense_sum = parseFloat(event.system_meta.billing.expenses_sum);
          // Add sum to expenses info
          if ("custom_sums" in (expense_info)) {
            expense_info["custom_sums"].sum += parseFloat(event.system_meta.billing.expenses_sum);
          } else {
            expense_info["custom_sums"] = {"name": "Custom sums", "type": "custom", "sum": parseFloat(event.system_meta.billing.expenses_sum)};
          }                    
          expenses_sum_from_event = true;
        }

        // Add sums to billed/unbilled
        self.total_sum += sum;
        if (event.system_meta.billing.status != "billed") {
          self.unbilled_sum += sum;
        } else {
          self.billed_sum += sum;
        }

        self.expenses_sum += expense_sum;

        this.event_count += 1;
        var e = {
          uuid: event.uuid,
          title: EventModel.title(event),
          subtitle: EventModel.subtitle(event),
          start_time: start_time,
          kickoff_time: kickoff_time,
          end_time: end_time,
          include_open_before: contract.include_open_before,
          state: event.state,
          output: EventModel.output_channel(event)||{title: ""},
          input: EventModel.input_channel(event)||{title: ""},
          contract_id: contract.id,
          contract_type: contract_type,
          contract_title: this.get_contract_title(contract),
          contract_last_billed: this.get_contract_last_billed(contract),
          sum: sum.toFixed(2),
          sum_from_event: sum_from_event,
          expenses_sum_from_event: expenses_sum_from_event,
          is_additional_information: is_additional_information,
          billing_status: event.system_meta.billing.status
        }
        ret.push(e);
        });
      this.billables_info = billable_info;
      this.expenses_info = expense_info;
      this.event_list_cached = ret;
      return ret;
    },  
    is_settings() {
      if (this.settings.length > 0) {
        this.update_contract_data();
        return true;
      }
      return false;
    },
    settings () {
      return SettingsModel.list("billing_config");
    },
    list_id() {
      return this.settings[0].value.contracts_list_id;
    },
    sorted_billables_info() {        
      var to_sort = this.billables_info;
      var sortable = [];
      for (var billable in to_sort) {
        sortable.push([billable, to_sort[billable].price, to_sort[billable].name, to_sort[billable].type, to_sort[billable].sum, to_sort[billable].number]);
      }
      // Sort to ascending and reverse it
      sortable.sort(function(a, b) {
        return a[4] - b[4];
      });
      sortable.reverse()

      let objSorted = {}
      sortable.forEach(function(item){
        objSorted[item[0]]={"price": item[1], "name": item[2], "type": item[3], "sum": item[4], "number": item[5]};
      })
      return objSorted;
    },
    sorted_expenses_info() {
      var to_sort = this.expenses_info;
      var sortable = [];
      for (var expense in to_sort) {
        sortable.push([expense, to_sort[expense].price, to_sort[expense].name, to_sort[expense].type, to_sort[expense].sum, to_sort[expense].number]);
      }
      
      // Sort to ascending and reverse it
      sortable.sort(function(a, b) {
        return a[1] - b[1];
      });
      sortable.reverse()


      let objSorted = {}
      sortable.forEach(function(item){
        objSorted[item[0]]={"price": item[1], "name": item[2], "type": item[3], "sum": item[4], "number": item[5]};
      })

      return objSorted;
    },
  },
  methods: {
    handleNewMonth(daterange) {
      this.filters.daterange.start = new Date(daterange[0] * 1000);
      this.filters.daterange.end = new Date(daterange[1] * 1000);
    },
    get_filtered_items() {
      return this.$refs.events.filteredItems;
    },
    toggleAll () {
      if (this.selected.length) this.selected = []
      else this.selected = this.event_list.slice()
    },
    changeSort (column) {
      if (this.pagination.sortBy === column) {
        this.pagination.descending = !this.pagination.descending
      } else {
        this.pagination.sortBy = column
        this.pagination.descending = false
      }
    },
    check_all_events () {
      let filtered_items = this.get_filtered_items();
      if (_.size(this.selected_events) == _.size(filtered_items)) {
        this.selected_events = {};
      } else {
        let ret = {};
        for (let key in filtered_items) {
          ret[filtered_items[key].uuid] = true;
        };
        this.selected_events = ret;
        //this.selected_events = this.event_list_cached;
      }
    },
    mark_events_as_billed (status) {
      var self = this;
      var events_to_mark = _.pickBy(this.selected_events, function(value, key) {
        return value;
      });

      if (_.size(events_to_mark) == 0) {return;}
      
      for (var key in events_to_mark) {
        self.ws_channel.push("set_billing_status", {event_uuid: key, status: status})
        .receive("ok", (msg) => {
          self.$message.success("Event was updated!");
        })
        .receive("error", (msg) => {
          self.$message.success("Unable to update event!");
        });
      }

      self.filter_events();
    },
    contract_color (type) {
      if (type == "event") return "success";
      if (type == "channel") return "primary";
    },
    update_contract_data (cb) {
      var url = "content_list/" + this.list_id;
      var self = this;
      Vue.http.get(url).then((response) => {
        self.contracts = {};
        self.contracts_by_customer = {};
        _.each(response.body, function(contract) {
          self.contracts[contract.id] = contract;
          if (typeof(contract.customer) != "undefined") {
            var customer_id = contract.customer.id;
            if (typeof(self.contracts_by_customer[customer_id]) == "undefined") {
              self.contracts_by_customer[customer_id] = [];
            }
            self.contracts_by_customer[customer_id].push(contract);
          }
        });
        if (typeof(cb) == "function") {
          cb();
        }
      }, (response) => {
      });
    },
    get_contract_by_id (id) {
      return _.find(this.contracts, {"id": id})
    },
    get_contract_last_billed (contract) {
      if (typeof(contract.id) != "undefined") {
        return contract.last_billed;
      }
      return 0;
    },
    get_contract_title (contract) {
      if (typeof(contract.id) != "undefined") {
        return contract.title;
      }
      return "";
    },
    format_date (v) {
      if (v != 0) {
        return datelib.format_date(v);
      }
    },
    format_time (v) {
      if (v != 0) {
        return datelib.format_time(v);
      }
    },
    event_duration(start_time, end_time, kickoff_time, include_open_before) {
      // Function to pad time
      function n(n) {
          return n > 9 ? "" + n: "0" + n;
      }
      var time_in_seconds;
      if (include_open_before) {
          time_in_seconds = end_time - start_time;
      } else {
          time_in_seconds = end_time - kickoff_time;
      }
      var minutes = n(Math.round(time_in_seconds % 3600 / 60));
      var hours = n(Math.floor(time_in_seconds / 3600))
      return (`${hours}:${minutes}`)
    }, 
    if_archived_color(state) {
      return state == 'archived' ? this.$dark() ? 'grey darken-2 grey--text' : 'grey lighten-4' : ''
    },
    open_edit_event (uuid) {
      this.$router.push("/billing/billing/event/"+uuid)
    },
    open_contract_edit (id) {
      this.$router.push("/billing/billing/content_edit/" + this.list_id + "/" + id);
    },
    filter_events () {
      this.table_loading = true;
      var _filters = _.cloneDeep(this.filters);
      if(typeof(_filters.daterange.start) != "undefined") {
        _filters.daterange.start = moment(_filters.daterange.start).startOf('day').unix();
      }
      if(typeof(_filters.daterange.end) != "undefined") {
        _filters.daterange.end = moment(_filters.daterange.end).endOf('day').unix();
      }
      delete _filters["contract"];
      this.ws_channel.push("filter", _filters);
    },
    connect_ws () {
      var self = this;
      this.ws_channel = this.$cloudserver.channel("events:all");
      this.ws_channel.on("init", function(msg) {
        self.filter_events();
      });
      this.ws_channel.on("events", function(msg) {
        self.events = msg.events;
        self.table_loading = false;
      });
      this.ws_channel.on("event-update", function(msg) {
        // FIXME: Pitäs päivittää vain oikea eventti eikä hakea kaikkea uusiksi...
        self.filter_events();
      });
      this.ws_channel.join();
    },
    toggle_chart () {
      this.show_doughnut_chart = !this.show_doughnut_chart;
    },
    export_csv () {
      var event_list = this.event_list;
      var rows = [];
      
      if (this.event_count == 0) {return;}
      var event;
      var contract;
      var row = [];
      var sum = 0;
      var contract_sum = 0;

      for (var e of event_list) {
        contract = this.get_contract_by_id(e.contract_id)
        row = [];
        row.push(contract.id);
        row.push(e.start_time);
        row.push('"' + `${contract.title}` + '"')
        row.push('"' + `${e.title}` + '"');
        row.push(this.event_duration(e.start_time, e.end_time, e.kickoff_time, e.include_open_before));

        // This way the static billable comes first
        // This should be optimized, maybe sort the array by the
        // included dict's type value
        sum = 0;
        if (e.sum_from_event) {
          row.push("Sum Defined In Event");
          row.push(e.sum);
          sum = e.sum;
        } else {
          // Add static prices to row
          for (var b of contract.billables) {
            if (b.data.type == "static") {
              row.push(b.data.name + " - " + b.data.type);
              row.push(String(parseFloat(b.data.price).toFixed(2)));
              sum += parseFloat(b.data.price)
            }
          }
          // Add hourly prices to row
          for (var b of contract.billables) {
            if (b.data.type == "hour") {
              var cost = 0.0;
              row.push(b.data.name + " - " + b.data.type);
              row.push(String(parseFloat(b.data.price).toFixed(2)));
              if (e.include_open_before) {
                cost = parseFloat(b.data.price) / 3600 * (e.end_time - e.start_time);
                row.push(String(cost.toFixed(2)));
              } else {
                cost = parseFloat(b.data.price) / 3600 * (e.end_time - e.kickoff_time);
                row.push(String(cost.toFixed(2)));
              }
              sum += cost;
            }
          }
        }

        row.push(String(parseFloat(sum).toFixed(2)));
        rows.push(row);
        // Array order:
        // Date, Title, Duration (For every event)
        // Static cost title, Static cost price (If static cost defined)
        // Hourly cost title, Hourly cost, Total Cost of the Hourly (If hourly costs defined)
        // Total cost for the event (For every event)
      }    

      // Sort rows by contract_id and start_time
      rows.sort(([a, b], [c, d]) => c - a || b - d);
      
      var events_by_contract = {};
      for (var r of rows) {
        r[1] = datelib.format_date(r[1]); // Format date from ts to string
        if (Object.keys(events_by_contract).includes(String(r[0]))) {
          events_by_contract[r[0]].push(r.slice(1)); // Remove contract_id from keys
        } else {
          events_by_contract[r[0]] = [];
          events_by_contract[r[0]].push(r.slice(1)); // Remove contract_id from keys
        }
      }

      var final_array = [];
      for (var c in events_by_contract) {
        var row = [];

        var contract = this.get_contract_by_id(parseInt(c));

        // Add headers
        row.push("Date");
        row.push("Contract");
        row.push("Event");
        row.push("Duration");
        for (var b of contract.billables) {
          if (b.data.type == "static") {
            row.push("Static Price Title");
            row.push("Static Price");
          }
        }
        for (var b of contract.billables) {
          if (b.data.type == "hour") {
            row.push("Hourly Price Title");
            row.push("Price Per Hour");
            row.push("Price For Event");
          }
        }

        row.push("Total Price For Event");
        final_array.push(row);
        
        // Add events under that
        row = [];
        contract_sum = 0;
        for (var e of events_by_contract[c]) {
          final_array.push(e); // Add newline to each event
          contract_sum += parseFloat(e[e.length - 1]); // Add event total to contract sum
        }

        // Add newline
        final_array.push(["Contract Total:", String(parseFloat(contract_sum).toFixed(2))]);
        final_array.push("");
      }

      // Siistitään viimeinen line tyhjäksi, ja laitetaan Total Price perään
      final_array.pop();
      final_array.push(["Total Price:", String(parseFloat(contract_sum).toFixed(2))]);

      // CSV-muotoon
      var csvContent = "data:text/csv;charset=utf-8,";
      for (var r of final_array) {
        if (r != "") {
          var row = r.join(",");
          csvContent += row + "\r\n";
        } else {
          csvContent += "\r\n";
        }
      }

      // Lataus koneelle
      var encodedUri = encodeURI(csvContent);
      var link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", "laskutus.csv");
      document.body.appendChild(link);
      link.click();
    },
  }
}

</script>

<style scoped>
.ks-billing-list-buttons {
  padding-top: 6px; 
  padding-bottom: 6px;
}
.ks-billing-table-headers-checkbox {
  padding-right: 5px; 
  padding-left: 10px; 
  width: 10px;
}
.ks-billing-table-item-title {
  padding: 0px 5px;
  width: 60px;
}
.ks-billing-table-item-subtitle {
  color: var(--ks-font-color-secondary); 
  font-size: 12px;
}
.report-card {
  width: 50% !important;
}
.v-input__slot {
  margin-bottom: 0px !important;
}

.v-messages {
  min-height: 0px !important;
}

.v-input--checkbox {
  justify-content: space-around;
}
.truncate {
  max-width: 400px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>